import React, { useState, useMemo } from 'react';
import './TradeRecordDialog.css';
import { getTradeRecord, saveTradeRecord as apiSaveTradeRecord } from './services/apiService';
import { ET_TIMEZONE } from './utils/marketUtils';
import anthropicLogo from './assets/icons/anthropic-logo.svg';
import openaiLogo from './assets/icons/openai-logo.svg';
import { AI_SOURCE } from './constants/ai';

// Helper function to display rating as stars (1-3 scale)
const getRatingStars = (rating) => {
  if (!rating) return '';
  return '⭐'.repeat(Math.min(rating, 3));
};

/**
 * Trade Record Dialog
 * Records trade entry/exit with market snapshot
 */
const TradeRecordDialog = ({
  stock,
  action, // 'entry' or 'exit'
  marketContext,
  aiAnthropicResult,
  aiOpenaiResult,
  onClose,
  onSave
}) => {
  const [actualPrice, setActualPrice] = useState(stock.currentPrice || '');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Touched state for onBlur validation
  const [priceTouched, setPriceTouched] = useState(false);
  const [quantityTouched, setQuantityTouched] = useState(false);

  // Field-level validation
  const isPriceValid = actualPrice === '' || parseFloat(actualPrice) > 0;
  const isQuantityValid = quantity === '' || parseInt(quantity) > 0;

  // Only show errors after field has been touched (blur)
  const showPriceError = priceTouched && !isPriceValid;
  const showQuantityError = quantityTouched && !isQuantityValid;

  // Find AI recommendations for this stock
  const claudePick = aiAnthropicResult?.topPicks?.find(p => p.symbol === stock.symbol);
  const gptPick = aiOpenaiResult?.topPicks?.find(p => p.symbol === stock.symbol);

  // Determine default value for followedRecommendation
  const getDefaultFollowed = useMemo(() => {
    if (claudePick && gptPick) {
      return null; // Both AIs recommend - user must choose
    } else if (claudePick) {
      return AI_SOURCE.CLAUDE;
    } else if (gptPick) {
      return AI_SOURCE.GPT;
    } else {
      return AI_SOURCE.MANUAL;
    }
  }, [claudePick, gptPick]);

  const [followedRecommendation, setFollowedRecommendation] = useState(getDefaultFollowed);

  const handleSave = async () => {
    // Validation
    if (!actualPrice || actualPrice <= 0) {
      setError('Please enter a valid price');
      return;
    }
    if (!quantity || quantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Store timestamp in UTC format (industry standard)
      // Display will convert to New York time
      const now = new Date();
      const timestamp = now.toISOString(); // "2026-01-24T14:31:00.000Z" (UTC)

      // Extract date in NY timezone for file organization
      const nyDate = now.toLocaleDateString('en-CA', { timeZone: ET_TIMEZONE }); // "2026-01-24"
      const date = nyDate;
      const month = date.substring(0, 7); // "2026-01"

      // Build trade record
      const tradeRecord = {
        id: `tr-${Date.now()}`,
        timestamp: timestamp,
        symbol: stock.symbol,
        action: action, // 'entry' or 'exit'

        // Trade details (manual input)
        actualPrice: parseFloat(actualPrice),
        quantity: parseInt(quantity),
        notes: notes.trim(),

        // Market snapshot (auto-captured)
        snapshot: {
          score: stock.score || 0,
          dailyGain: stock.dailyGainPercentage || 0,
          avgChange: stock.avgChangePercentage || 0,
          displayPrice: stock.currentPrice || 0,
          volume: stock.volume || 0,
          accelerationArrow: stock.accelerationArrow || '',

          // Market context
          marketStatus: marketContext?.status || 'unknown',
          marketSentiment: marketContext?.sentiment || 'unknown',
          indices: {
            SPY: marketContext?.indices?.SPY || 0,
            QQQ: marketContext?.indices?.QQQ || 0,
            DIA: marketContext?.indices?.DIA || 0,
            IWM: marketContext?.indices?.IWM || 0
          }
        },

        // AI recommendations (if available)
        aiRecommendations: {
          // Claude AI Analysis
          claudeRecommended: !!claudePick,
          claudeMarketSummary: aiAnthropicResult?.summary || null,
          claudeMarketAnalysis: aiAnthropicResult?.analysis || null,  // Complete analysis text
          claudeRank: claudePick?.rank || null,
          claudeRating: claudePick?.rating || null,
          claudeTrend: claudePick?.trend || null,
          claudePattern: claudePick?.pattern || null,
          claudeAction: claudePick?.action || null,

          // GPT AI Analysis
          gptRecommended: !!gptPick,
          gptMarketSummary: aiOpenaiResult?.summary || null,
          gptMarketAnalysis: aiOpenaiResult?.analysis || null,  // Complete analysis text
          gptRank: gptPick?.rank || null,
          gptRating: gptPick?.rating || null,
          gptTrend: gptPick?.trend || null,
          gptPattern: gptPick?.pattern || null,
          gptAction: gptPick?.action || null,

          // Which recommendation was followed (for accurate AI Review analysis)
          followedRecommendation: followedRecommendation,
          followedPattern: followedRecommendation === AI_SOURCE.CLAUDE ? claudePick?.pattern :
                          followedRecommendation === AI_SOURCE.GPT ? gptPick?.pattern :
                          followedRecommendation === AI_SOURCE.BOTH ? (claudePick?.pattern || gptPick?.pattern) :
                          null,
          followedSource: followedRecommendation
        },

        // Metadata
        brokerRecordLinked: false,
        dateOnly: date
      };

      // Save to file system or localStorage
      await saveTradeRecord(date, month, tradeRecord);

      // Notify parent
      if (onSave) {
        onSave(tradeRecord);
      }

      // Close dialog
      onClose();

    } catch (err) {
      console.error('Failed to save trade record:', err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const saveTradeRecord = async (date, month, tradeRecord) => {
    // Read existing day data from API
    let dayData;
    try {
      const resp = await getTradeRecord(date);
      dayData = resp?.data ? (typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data) : null;
    } catch {
      dayData = null;
    }

    if (!dayData) {
      dayData = { date, trades: [] };
    }

    // Add new record
    dayData.trades.push(tradeRecord);

    // Save via API
    await apiSaveTradeRecord(date, dayData);
    console.log(`✅ Trade record saved to DB: ${date}`);
  };

  return (
    <div className="trade-record-overlay" onClick={onClose}>
      <div className="trade-record-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="trade-record-header">
          <h2>
            {action === 'entry' ? '📝 Record Entry' : '⬆️ Record Exit'} - {stock.symbol}
          </h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="trade-record-content">
          {/* Error message */}
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {/* 1. Trade Details Section (Required fields) */}
          <section className="input-section">
            <h3>Trade Details</h3>

            <div className="form-group">
              <label>Actual Price *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={actualPrice}
                onChange={(e) => setActualPrice(e.target.value)}
                onBlur={() => setPriceTouched(true)}
                placeholder="142.35"
                autoFocus
                className={showPriceError ? 'input-error' : ''}
              />
              <span className="hint">UI Price: ${stock.currentPrice?.toFixed(2)}</span>
              {showPriceError && <span className="field-error">Price must be greater than 0</span>}
            </div>

            <div className="form-group">
              <label>Quantity *</label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onBlur={() => setQuantityTouched(true)}
                placeholder="100"
                className={showQuantityError ? 'input-error' : ''}
              />
              {showQuantityError && <span className="field-error">Quantity must be at least 1</span>}
            </div>

            {/* AI Recommendation Selector - only for Entry, not Exit */}
            {action === 'entry' && (claudePick || gptPick) && (
              <div className="form-group">
                <label>Which recommendation are you following?</label>
                <div className="followed-selector">
                  <div className="followed-cards">
                    {claudePick && (
                      <div
                        className={`followed-card claude ${followedRecommendation === AI_SOURCE.CLAUDE ? 'selected' : ''}`}
                        onClick={() => setFollowedRecommendation(AI_SOURCE.CLAUDE)}
                      >
                        <img src={anthropicLogo} alt="Claude" className="card-logo" />
                        <div className="card-label">Claude {getRatingStars(claudePick.rating)}</div>
                        <div className="card-pattern">{claudePick.pattern}</div>
                        {followedRecommendation === AI_SOURCE.CLAUDE && <div className="selected-badge">✓</div>}
                      </div>
                    )}

                    {gptPick && (
                      <div
                        className={`followed-card gpt ${followedRecommendation === AI_SOURCE.GPT ? 'selected' : ''}`}
                        onClick={() => setFollowedRecommendation(AI_SOURCE.GPT)}
                      >
                        <img src={openaiLogo} alt="GPT" className="card-logo" />
                        <div className="card-label">GPT {getRatingStars(gptPick.rating)}</div>
                        <div className="card-pattern">{gptPick.pattern}</div>
                        {followedRecommendation === AI_SOURCE.GPT && <div className="selected-badge">✓</div>}
                      </div>
                    )}

                    {claudePick && gptPick && (
                      <div
                        className={`followed-card both ${followedRecommendation === AI_SOURCE.BOTH ? 'selected' : ''}`}
                        onClick={() => setFollowedRecommendation(AI_SOURCE.BOTH)}
                      >
                        <div className="card-logos">
                          <img src={anthropicLogo} alt="Claude" className="card-logo-small" />
                          <span className="plus">+</span>
                          <img src={openaiLogo} alt="GPT" className="card-logo-small" />
                        </div>
                        <div className="card-label">Both AIs</div>
                        <div className="card-pattern">(consensus)</div>
                        {followedRecommendation === AI_SOURCE.BOTH && <div className="selected-badge">✓</div>}
                      </div>
                    )}

                    <div
                      className={`followed-card manual ${followedRecommendation === AI_SOURCE.MANUAL ? 'selected' : ''}`}
                      onClick={() => setFollowedRecommendation(AI_SOURCE.MANUAL)}
                    >
                      <div className="card-icon">📊</div>
                      <div className="card-label">My own analysis</div>
                      <div className="card-pattern">(not AI)</div>
                      {followedRecommendation === AI_SOURCE.MANUAL && <div className="selected-badge">✓</div>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Entry reason, position sizing notes..."
                rows={3}
              />
            </div>
          </section>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : '💾 Save Record'}
            </button>
            <button
              className="btn-cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeRecordDialog;
